import { useCallback, useEffect, useRef } from "react"

import { router, useLocalSearchParams } from "expo-router"

import { ChannelSplash } from "src/components/ChannelSplash"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { useUnauthChannelInfoQuery } from "src/queries/useUnauthChannelInfo"
import { ChannelBrandData } from "src/services/api/api.types"

/**
 * Shown immediately when an unauthenticated user arrives via a channel deeplink.
 * Delegates all display/timing/animation to ChannelSplash (same as the auth flow).
 * On dismiss routes based on channel_info:
 *   - is_media_mode !== false  →  /unauth/channel-library/[id]
 *   - is_media_mode === false  →  /unauth/login
 *   - error / not found        →  /unauth/login
 */
export default function UnauthChannelSplashScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const { data: channelInfo, isError } = useUnauthChannelInfoQuery(id ?? null)
  const { setPendingChannelId } = useChannelLink()

  // Sync the path ID into ChannelLinkContext so MobileAppBanner has the correct
  // channel for its deep link when the user arrives via a universal link directly
  // to this route rather than via the ?channel= query-param flow.
  useEffect(() => {
    if (id) setPendingChannelId(id)
  }, [id, setPendingChannelId])

  // Use a ref so onDismiss stays stable and doesn't restart ChannelSplash's timers
  // when channelInfo arrives asynchronously during the splash display.
  const channelInfoRef = useRef<ChannelBrandData | null | undefined>(undefined)
  const isErrorRef = useRef(false)
  channelInfoRef.current = channelInfo
  isErrorRef.current = isError

  const handleDismiss = useCallback(() => {
    const info = channelInfoRef.current
    const error = isErrorRef.current

    if (error || info === null || info === undefined) {
      router.replace("/unauth/login")
      return
    }

    if (info.is_media_mode !== false) {
      router.replace({
        pathname: "/unauth/channel-library/[id]",
        params: { id },
      } as any)
    } else {
      router.replace("/unauth/login")
    }
  }, [id])

  // Pass null while data is loading — ChannelSplash renders a white screen until
  // brandData arrives, then waits for the image to load before starting its hold timer.
  return <ChannelSplash brandData={channelInfo ?? null} onDismiss={handleDismiss} />
}
